import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { State } from 'src/app/components/designer/models/state';
import { Step } from 'src/app/components/designer/models/step';
import { StepSpecificStateConditionData } from 'src/app/components/designer/models/step-specific-state-condition-datas';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ContextualMenuEvent } from 'src/app/controls/workflows/contextual-menu-event';
import { NeedSteps } from 'src/app/controls/workflows/need-steps';
import { WorkflowDataInterface } from 'src/app/controls/workflows/workflow-data.interface';

@Component({
    selector: 'app-workflow-condition-step-specific-state-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, FaIconComponent, MatTooltipModule],
    templateUrl: './workflow-condition-step-specific-state-data.component.html',
    styleUrl: './workflow-condition-step-specific-state-data.component.scss'
})
export class WorkflowConditionStepSpecificStateDataComponent implements WorkflowDataInterface, OnInit, NeedSteps {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: StepSpecificStateConditionData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | StepSpecificStateConditionData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as StepSpecificStateConditionData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): StepSpecificStateConditionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des étapes du workflow.
     */
    public _steps: Step[];

    /**
     * Spécifie la liste des étapes du workflow.
     */
    set steps(value: Step[]) {
        this._steps = value;
        this.fillForm();
    }

    /**
     * Liste des états de l'étape selectionnée.
     */
    public _states: State[];

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Constructeur.
     */
    constructor() {
        console.log('WorkflowConditionStepSpecificStateDataComponent - constructor : ');
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        console.log('WorkflowConditionStepSpecificStateDataComponent - ngOnInit : ');
        this.dataForm = new UntypedFormGroup({
            step: new UntypedFormControl([], [Validators.required]),
            state: new UntypedFormControl([], [Validators.required]),
        });

        if (this.mode === FormMode.Reading) {
            this.dataForm.disable();
        } else if (this.mode === FormMode.Creation || this.mode === FormMode.Edition) {
            this.dataForm.enable();
        }

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return this.dataForm.valid;
    }

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Appellée lorsque l'utilisateur selectionne une étape.
     * @param event : évènement contenant l'étape selectionnée.
     */
    public onStepChanged(event: MatSelectChange) {
        const selectedStep: Step = this._steps.find((x) => x.id == event.value);
        this._states = selectedStep.data.states;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data) {
                this.dataForm.get('step').setValue(this._data.stepId);

                if (this._states) {
                    this.dataForm.get('state').setValue(this._data.stateId);
                }

                if (this._steps) {
                    if (this._data.stepId) {
                        const selectedStep: Step = this._steps.find((x) => x.id == this._data.stepId);
                        this._states = selectedStep.data.states;
                    }

                    this.dataForm.get('state').setValue(this._data.stateId);
                }
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: StepSpecificStateConditionData = new StepSpecificStateConditionData();
        updatedData.stepId = this.dataForm.get('step').value;
        updatedData.stateId = this.dataForm.get('state').value;

        this._data = updatedData;
    }
}
